import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { t } from '../../utils/translation';

/**
 * Custom Select Component
 * @param {string} label - Label for the select input
 * @param {string} value - Current selected value
 * @param {function} onChange - Callback when value changes
 * @param {Array} options - Array of { value, label } objects
 */
export default function Select({ label, value, onChange, options, error }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value) || options[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    return (
        <div className="form-group" ref={containerRef}>
            {label && <label className="form-label">{label}</label>}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`custom-select-trigger ${isOpen ? 'open' : ''} ${error ? 'border-red-600' : ''}`}
                >
                    <span className={`block truncate ${!selectedOption ? 'text-gray-400' : 'text-gray-900'}`}>
                        {selectedOption ? selectedOption.label : t('UI.SELECT_PLACEHOLDER')}
                    </span>
                    <ChevronDown
                        size={16}
                        className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                </button>

                {isOpen && (
                    <div className="custom-select-menu">
                        {options.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => handleSelect(option.value)}
                                className={`custom-select-option ${option.value === value ? 'selected' : ''}`}
                            >
                                <span>{option.label}</span>
                                {option.value === value && <Check size={16} />}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {error && <p className="form-error mt-1">{error}</p>}
        </div>
    );
}
